'use client'

import { useState, useEffect, useCallback } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'

export interface PrewarmedIntent {
  clientSecret: string
  paymentIntentId: string
  amountCents: number
  type: 'event_tip' | 'artist_tip'
}

interface StripePreloadState {
  stripe: Stripe | null
  isLoading: boolean
  error: string | null
  retry: () => void
}

// Track if we've already started loading (client-side only)
let stripeLoadPromise: Promise<Stripe | null> | null = null
let loadAttempts = 0
const MAX_LOAD_ATTEMPTS = 3

// Default prewarm amount ($5)
export const STRIPE_PREWARM_AMOUNT_CENTS = 500

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

async function loadStripeWithRetry(): Promise<Stripe | null> {
  // Only load Stripe in browser environment
  if (!isBrowser) {
    return null
  }

  const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
  
  if (!publishableKey) {
    throw new Error('Stripe publishable key not configured')
  }

  try {
    const stripe = await loadStripe(publishableKey)
    if (!stripe) {
      throw new Error('Stripe failed to initialize')
    }
    return stripe
  } catch (error) {
    loadAttempts++
    if (loadAttempts < MAX_LOAD_ATTEMPTS) {
      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 1000 * loadAttempts))
      return loadStripeWithRetry()
    }
    throw error
  }
}

// Report error to the platform
async function reportStripeError(error: string, context: Record<string, unknown> = {}) {
  try {
    // Log to console for debugging
    console.error('[Stripe Preload Error]', error, context)
    
    // Report to platform API
    await fetch('/api/visitor', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'stripe_load_error',
        error,
        context: {
          ...context,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          timestamp: new Date().toISOString(),
        },
      }),
    }).catch(() => {
      // Silently fail if reporting fails
    })
  } catch {
    // Silently fail
  }
}

export function useStripePreload(artistId?: string, eventId?: string): StripePreloadState {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadStripeInstance = useCallback(async () => {
    // Check if Stripe key is configured
    if (!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
      setError('Payment system not configured')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Use shared promise to avoid multiple loads
      if (!stripeLoadPromise) {
        loadAttempts = 0
        stripeLoadPromise = loadStripeWithRetry()
      }

      const stripeInstance = await stripeLoadPromise
      setStripe(stripeInstance)
      setError(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load payment system'
      setError(errorMessage)
      setStripe(null)
      
      // Reset the promise so retry can work
      stripeLoadPromise = null
      
      // Report the error
      reportStripeError(errorMessage, { artistId, eventId })
    } finally {
      setIsLoading(false)
    }
  }, [artistId, eventId])

  useEffect(() => {
    loadStripeInstance()
  }, [loadStripeInstance])

  const retry = useCallback(() => {
    stripeLoadPromise = null
    loadAttempts = 0
    loadStripeInstance()
  }, [loadStripeInstance])

  return { stripe, isLoading, error, retry }
}

// Export the stripe promise for components that need it directly
// This is safe to call at module level as it only initializes when actually called in the browser
export function getStripePromise(): Promise<Stripe | null> | null {
  // Return null on server - Stripe can only be loaded in browser
  if (!isBrowser) {
    return null
  }

  if (!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
    return null
  }
  
  if (!stripeLoadPromise) {
    loadAttempts = 0
    stripeLoadPromise = loadStripeWithRetry()
  }
  
  return stripeLoadPromise
}

